import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import categoryService from '../../services/categoryService';

const Categories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setLoading(true);
                const response = await categoryService.getCategories();
                if (response.success) {
                    // Take first 3 categories for display
                    const categoriesToDisplay = response.categories.slice(0, 3);

                    // Fetch first product for each category to get thumbnail image
                    const categoriesWithImages = await Promise.all(
                        categoriesToDisplay.map(async (cat) => {
                            try {
                                // Fetch products for this category
                                const productsResponse = await categoryService.getCategoryProducts(cat._id);

                                // Get first product's first image, or use fallback
                                let image = 'https://images.pexels.com/photos/1183266/pexels-photo-1183266.jpeg?auto=compress&cs=tinysrgb&w=400';

                                if (productsResponse.success && productsResponse.products && productsResponse.products.length > 0) {
                                    const firstProduct = productsResponse.products[0];
                                    if (firstProduct.images && firstProduct.images.length > 0 && firstProduct.images[0].path) {
                                        image = firstProduct.images[0].path;
                                    }
                                }

                                return {
                                    ...cat,
                                    thumbnailImage: image
                                };
                            } catch (err) {
                                // If fetching products fails, use fallback image
                                console.error(`Error fetching products for category ${cat._id}:`, err);
                                return {
                                    ...cat,
                                    thumbnailImage: 'https://images.pexels.com/photos/1183266/pexels-photo-1183266.jpeg?auto=compress&cs=tinysrgb&w=400'
                                };
                            }
                        })
                    );

                    setCategories(categoriesWithImages);
                } else {
                    setError('Failed to load categories');
                }
            } catch (err) {
                setError('Error loading categories');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    // Skeleton loader component
    const CategorySkeleton = () => (
        <div className="relative rounded-lg overflow-hidden animate-pulse">
            <div className="w-full h-[400px] bg-gray-200"></div>
            <div className="absolute bottom-4 left-4">
                <div className="h-8 w-24 bg-gray-300 rounded"></div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <section className="bg-gray-50 py-16">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-sm font-semibold text-gray-600 mb-8 tracking-wider">SHOP BY CATEGORIES</h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <CategorySkeleton />
                        <CategorySkeleton />
                        <CategorySkeleton />
                        <div className="bg-white rounded-lg flex flex-col items-center justify-center p-8 h-[400px] border border-gray-200">
                            <div className="h-6 w-20 bg-gray-200 rounded mb-2 animate-pulse"></div>
                            <div className="h-4 w-32 bg-gray-200 rounded mb-6 animate-pulse"></div>
                            <div className="w-12 h-12 border-2 border-gray-200 rounded-full"></div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="bg-gray-50 py-16">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-sm font-semibold text-gray-600 mb-8 tracking-wider">SHOP BY CATEGORIES</h2>
                    <div className="flex justify-center items-center h-[400px]">
                        <div className="text-red-600">{error}</div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="bg-gray-50 py-16">
            <div className="max-w-7xl mx-auto px-6">
                <h2 className="text-sm font-semibold text-gray-600 mb-8 tracking-wider">SHOP BY CATEGORIES</h2>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {categories.map((category) => (
                        <Link
                            key={category._id}
                            to={`/category/${category._id}`}
                            className="relative group cursor-pointer overflow-hidden rounded-lg"
                        >
                            <img
                                src={category.thumbnailImage || "https://images.pexels.com/photos/1183266/pexels-photo-1183266.jpeg?auto=compress&cs=tinysrgb&w=400"}
                                alt={category.name || category.category || 'Category'}
                                className="w-full h-[400px] object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                            <div className="absolute bottom-4 left-4 bg-white px-4 py-2 rounded shadow-sm">
                                <span className="text-sm font-medium">{category.name || category.category}</span>
                            </div>
                        </Link>
                    ))}

                    <Link
                        to="/collections"
                        className="bg-white rounded-lg flex flex-col justify-end items-start p-8 h-[400px] border border-gray-200 hover:border-black transition group"
                    >
                        <h3 className="text-2xl font-normal mb-2 text-gray-900">Discover</h3>
                        <p className="text-base text-gray-900 mb-6">all new items</p>
                        <div className="w-16 h-16 border border-black rounded-full flex items-center justify-center group-hover:bg-black group-hover:text-white transition-all">
                            <ArrowRight size={28} strokeWidth={2.5} className="transform rotate-[-45deg]" />
                        </div>
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default Categories;
